// @ts-strict-ignore
import * as React from 'react';
import { useUserCanPerformAction } from '@/utils/hooks/useUserCanPerformAction';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { getIsAppleAppStore, isAndroidPlatform } from '../../helpers';
import {
    BaseUpdateReleaseRequest,
    DeleteReleaseRequest,
    SkipReleaseRequest,
    TRY_DELETE_RELEASE_ENTITY,
    tryDeleteRelease,
    tryUpdateRelease,
} from '../../actions/release';
import { AppRouteComponentMatchParams, Nullable, ReleaseComponentProps } from '../../typescriptHelpers';
import { DeleteOutlined, VerticalLeftOutlined, CheckCircleOutlined, UndoOutlined } from '@ant-design/icons';
import CreateEditReleaseModal from '../CreateEditReleaseModal';
import { globalMessage } from '../../utils/globalMessage';
import { createAppRoute } from '../../helpers/routeHelpers';
import Modal from 'antd/lib/modal';
import { ReleaseUpdatable } from '../../actions/updatable';
import { ReleaseStatus, ReleaseType, TargetVersionInfo } from '../../types';
import PrepareHotfixOrRollbackReleaseModal from '../rollbacks/PrepareHotfixOrRollbackReleaseModal';
import { PrepareNewWorkflowRunModal } from '../prepareRelease/PrepareNewWorkflowRunModal';
import * as Storage from '../../helpers/localStorageHelper';
import Dropdown from 'antd/lib/dropdown';
import Tooltip from 'antd/lib/tooltip';
import { runwayColors } from '../styles/globalStyles';
import { ItemType } from 'antd/lib/menu/interface';
import { useError, useIsLoading, useUpdated, useAppPlatform } from '@/utils/hooks/helpers';
import { SkipReleaseModal } from '../SkipReleaseModal';
import { RootState } from '../../reducers/reducers';
import { selectAppStoreType, selectIsWorkflowsSSRStepsEnabled } from '../../selectors/selectors';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import { trackReleaseRouteEvent } from '../../actions/trackEvent';
import * as AnalyticEvents from '../../utils/analytics';

enum EditReleaseOption {
    Complete,
    Uncomplete,
    Skip,
    Delete,
    Unskip,
}

interface ReleaseActionsComponentProps extends ReleaseComponentProps {
    version: string;
    releaseType: ReleaseType;
    releaseStatus: ReleaseStatus;
    rollbackTargetVersionInfo?: TargetVersionInfo;
    editReleaseDisabled: boolean;
    deleteReleaseDisabled: boolean;
    skipReleaseDisabled: boolean;
    canBeSubmitted: Nullable<boolean>;
    canRevertCompletedStatus: Nullable<boolean>;
}

const ReleaseActionsComponent: React.FC<ReleaseActionsComponentProps> = (props: ReleaseActionsComponentProps) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const { orgIdParam, appIdParam } = useParams<AppRouteComponentMatchParams>();
    const [modalVisible, setModalVisible] = React.useState(false);
    const [skipReleaseModalVisible, setSkipReleaseModalVisible] = React.useState(false);

    const appPlatform = useAppPlatform({ appId: props.appId, orgId: props.orgId });
    const isAndroid = isAndroidPlatform(appPlatform);

    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, props.appId));
    const isAppleAppStore = getIsAppleAppStore(appStoreType);

    const userCanUnskipRelease = useUserCanPerformAction(
        RunwayUserAction.UACreateSkipDeleteCompleteReleases,
        props.releaseId,
        undefined,
        undefined,
        undefined,
        props.appId,
    );

    const isSSRMigrated = useSelector((state: RootState) => selectIsWorkflowsSSRStepsEnabled(state, props.appId));

    const deleteReleaseIsLoading = useIsLoading(TRY_DELETE_RELEASE_ENTITY, props.releaseId);
    const deleteReleaseError = useError(TRY_DELETE_RELEASE_ENTITY, props.releaseId);
    const deleteReleaseSuccess = useUpdated(TRY_DELETE_RELEASE_ENTITY, props.releaseId);

    const skipReleaseIsLoading = useIsLoading(ReleaseUpdatable.MarkReleaseSkipped, props.releaseId);
    const skipReleaseError = useError(ReleaseUpdatable.MarkReleaseSkipped, props.releaseId);
    const skipReleaseSuccess = useUpdated(ReleaseUpdatable.MarkReleaseSkipped, props.releaseId);

    const completeReleaseIsLoading = useIsLoading(ReleaseUpdatable.MarkReleaseComplete, props.releaseId);
    const completeReleaseError = useError(ReleaseUpdatable.MarkReleaseComplete, props.releaseId);
    const completeReleaseSuccess = useUpdated(ReleaseUpdatable.MarkReleaseComplete, props.releaseId);

    const uncompleteReleaseIsLoading = useIsLoading(ReleaseUpdatable.MarkReleaseUncomplete, props.releaseId);
    const uncompleteReleaseError = useError(ReleaseUpdatable.MarkReleaseUncomplete, props.releaseId);
    const uncompleteReleaseSuccess = useUpdated(ReleaseUpdatable.MarkReleaseUncomplete, props.releaseId);

    const unskipReleaseIsLoading = useIsLoading(ReleaseUpdatable.MarkReleaseUnskipped, props.releaseId);
    const unskipReleaseError = useError(ReleaseUpdatable.MarkReleaseUnskipped, props.releaseId);
    const unskipReleaseSuccess = useUpdated(ReleaseUpdatable.MarkReleaseUnskipped, props.releaseId);

    const isRollback = props.releaseType === ReleaseType.Rollback;

    const showConfirm = (option: EditReleaseOption) => {
        let verb = '';
        let action = null;

        switch (option) {
            case EditReleaseOption.Complete:
                verb = 'complete';
                action = completeRelease;
                break;
            case EditReleaseOption.Uncomplete:
                verb = 'un-complete';
                action = uncompleteRelease;
                break;
            case EditReleaseOption.Delete:
                verb = isRollback ? 'discard' : 'delete';
                action = deleteRelease;
                break;
            case EditReleaseOption.Unskip:
                verb = 'un-skip';
                action = unskipRelease;
                break;
            case EditReleaseOption.Skip:
                setSkipReleaseModalVisible(true);
                return;
        }

        Modal.confirm({
            title: `Are you sure you want to ${verb} this release?`,
            icon: null,
            content: confirmationModalText(option, isRollback, props.version),
            okText: `Yes, ${verb} release`,
            okType: option === EditReleaseOption.Delete ? 'danger' : 'default',
            okButtonProps: {
                type: 'primary',
            },
            cancelText: 'Nevermind',
            onOk: action,
            maskClosable: true,
        });
    };

    const completeRelease = () => {
        const request: BaseUpdateReleaseRequest = {
            orgId: props.orgId,
            appId: props.appId,
            releaseId: props.releaseId,
        };

        dispatch(tryUpdateRelease(ReleaseUpdatable.MarkReleaseComplete, request, props.releaseId));
    };

    const uncompleteRelease = () => {
        const request: BaseUpdateReleaseRequest = {
            orgId: props.orgId,
            appId: props.appId,
            releaseId: props.releaseId,
        };

        dispatch(tryUpdateRelease(ReleaseUpdatable.MarkReleaseUncomplete, request, props.releaseId));
    };

    const deleteRelease = () => {
        const request: DeleteReleaseRequest = {
            orgId: props.orgId,
            appId: props.appId,
            releaseId: props.releaseId,
            orgIdParam: orgIdParam,
            appIdParam: appIdParam,
        };

        dispatch(tryDeleteRelease(request));
    };

    const markReleaseSkipped = (
        shouldRollForwardMetadata: boolean = false,
        shouldDevRejectRelease: boolean = false,
    ) => {
        const request: SkipReleaseRequest = {
            orgId: props.orgId,
            appId: props.appId,
            releaseId: props.releaseId,
            shouldRollForwardMetadata,
            shouldDevRejectRelease,
        };

        dispatch(
            trackReleaseRouteEvent({
                orgId: props.orgId,
                appId: props.appId,
                releaseId: props.releaseId,
                eventName: AnalyticEvents.Event.SkipRelease,
            }),
        );
        dispatch(tryUpdateRelease(ReleaseUpdatable.MarkReleaseSkipped, request, props.releaseId));
    };

    const unskipRelease = () => {
        const request: BaseUpdateReleaseRequest = {
            orgId: props.orgId,
            appId: props.appId,
            releaseId: props.releaseId,
        };

        dispatch(
            trackReleaseRouteEvent({
                orgId: props.orgId,
                appId: props.appId,
                releaseId: props.releaseId,
                eventName: AnalyticEvents.Event.UnskipRelease,
            }),
        );
        dispatch(tryUpdateRelease(ReleaseUpdatable.MarkReleaseUnskipped, request, props.releaseId));
    };

    const handleSkipReleaseModalConfirm = (shouldRollForwardMetadata: boolean, shouldDevRejectRelease: boolean) => {
        setSkipReleaseModalVisible(false);
        markReleaseSkipped(shouldRollForwardMetadata, shouldDevRejectRelease);
    };

    const handleSkipReleaseModalCancel = () => {
        setSkipReleaseModalVisible(false);
    };

    React.useEffect(() => {
        if (deleteReleaseError) {
            globalMessage.error(deleteReleaseError);
        }
    }, [deleteReleaseError]);

    React.useEffect(() => {
        if (deleteReleaseSuccess) {
            globalMessage.success(
                `${isRollback ? 'Rollback' : 'Release'} ${props.version} successfully ${
                    isRollback ? 'discarded' : 'deleted'
                }!`,
            );

            const appRoute = createAppRoute(orgIdParam, appIdParam);

            history.replace(appRoute);

            Storage.clearLastSelectedReleaseStepForRelease(props.releaseId);
        }
    }, [deleteReleaseSuccess]);

    React.useEffect(() => {
        if (skipReleaseError) {
            globalMessage.error(skipReleaseError);
        }
    }, [skipReleaseError]);

    React.useEffect(() => {
        if (skipReleaseSuccess) {
            globalMessage.success(
                `${isRollback ? 'Rollback' : 'Release'} ${props.version} has been marked as skipped.`,
            );
        }
    }, [skipReleaseSuccess]);

    React.useEffect(() => {
        if (completeReleaseSuccess) {
            globalMessage.success(`Release ${props.version} has been completed.`);
        }
    }, [completeReleaseSuccess]);

    React.useEffect(() => {
        if (completeReleaseError) {
            globalMessage.error(completeReleaseError);
        }
    }, [completeReleaseError]);

    React.useEffect(() => {
        if (uncompleteReleaseSuccess) {
            globalMessage.success(`Release ${props.version} has been un-completed.`);
        }
    }, [uncompleteReleaseSuccess]);

    React.useEffect(() => {
        if (uncompleteReleaseError) {
            globalMessage.error(uncompleteReleaseError);
        }
    }, [uncompleteReleaseError]);

    React.useEffect(() => {
        if (unskipReleaseSuccess) {
            globalMessage.success(`Release ${props.version} has been un-skipped.`);
        }
    }, [unskipReleaseSuccess]);

    React.useEffect(() => {
        if (unskipReleaseError) {
            globalMessage.error(unskipReleaseError);
        }
    }, [unskipReleaseError]);

    const onMenuClick = (info) => {
        const key = parseInt(info.key) as EditReleaseOption;

        showConfirm(key);
    };

    const items: ItemType[] = [
        {
            key: EditReleaseOption.Complete,
            label: 'Complete release',
            disabled: props.skipReleaseDisabled,
            icon: <CheckCircleOutlined style={{ color: runwayColors.statusGreen }} />,
        },
        ...(isAndroid && props.releaseStatus === ReleaseStatus.Completed ?
            [
                {
                    key: EditReleaseOption.Uncomplete,
                    label:
                        !props.canRevertCompletedStatus ?
                            <Tooltip
                                title={`Please make sure there's no Release on the Production track in Google Play with version ${props.version}`}
                            >
                                <span>Un-complete release</span>
                            </Tooltip>
                        :   'Un-complete release',
                    disabled: !props.canRevertCompletedStatus,
                    icon: <UndoOutlined style={{ color: runwayColors.statusYellow }} />,
                },
            ]
        :   []),
        ...(props.releaseStatus === ReleaseStatus.Skipped ?
            [
                {
                    key: EditReleaseOption.Unskip,
                    label: 'Un-skip release',
                    disabled: !userCanUnskipRelease,
                    icon: <UndoOutlined style={{ color: runwayColors.statusYellow }} />,
                },
            ]
        :   []),
        ...(props.releaseStatus !== ReleaseStatus.Skipped ?
            [
                {
                    key: EditReleaseOption.Skip,
                    label: 'Skip release',
                    disabled: props.skipReleaseDisabled && !isAndroid,
                    icon: <VerticalLeftOutlined />,
                },
            ]
        :   []),
        {
            key: EditReleaseOption.Delete,
            label: 'Delete release',
            disabled: props.deleteReleaseDisabled,
            icon: <DeleteOutlined />,
            danger: true,
        },
    ];

    const rollbackItems: ItemType[] = [
        {
            key: EditReleaseOption.Skip,
            label: 'Skip rollback',
            disabled: props.skipReleaseDisabled,
            icon: <VerticalLeftOutlined />,
        },
        {
            key: EditReleaseOption.Delete,
            label: 'Discard rollback',
            disabled: props.deleteReleaseDisabled,
            icon: <DeleteOutlined />,
            danger: true,
        },
    ];

    const isRollbackTargetNotComplete =
        isRollback && props.rollbackTargetVersionInfo?.status !== ReleaseStatus.Completed;

    return (
        <div>
            <Dropdown.Button
                menu={{ items: isRollback ? rollbackItems : items, onClick: onMenuClick }}
                onClick={() => setModalVisible(true)}
                loading={
                    deleteReleaseIsLoading ||
                    skipReleaseIsLoading ||
                    completeReleaseIsLoading ||
                    uncompleteReleaseIsLoading ||
                    unskipReleaseIsLoading
                }
                disabled={isRollbackTargetNotComplete}
            >
                {isRollback ? 'Convert to roll-forward hotfix' : 'Edit release'}
            </Dropdown.Button>
            {isSSRMigrated ?
                <PrepareNewWorkflowRunModal
                    orgId={props.orgId}
                    appId={props.appId}
                    open={modalVisible}
                    mode={isRollback ? 'hotfix' : 'standard'}
                    isEditingRelease={!isRollback}
                    releaseId={props.releaseId}
                    isConvertingRollback={isRollback}
                    releaseVersion={props.version}
                    displayOnlyTargetDates={false}
                    canBeSubmitted={props.canBeSubmitted}
                    onCancel={() => setModalVisible(false)}
                    onSuccess={() => setModalVisible(false)}
                />
            : isRollback ?
                <PrepareHotfixOrRollbackReleaseModal
                    orgId={props.orgId}
                    appId={props.appId}
                    releaseId={props.releaseId}
                    open={modalVisible}
                    releaseVersion={props.version}
                    isConvertingRollback={true}
                    onCancel={() => setModalVisible(false)}
                />
            :   <CreateEditReleaseModal
                    orgId={props.orgId}
                    appId={props.appId}
                    open={modalVisible}
                    onDidCreateOrEditRelease={() => {
                        setModalVisible(false);
                    }}
                    onCancel={() => {
                        setModalVisible(false);
                    }}
                    isEditingRelease={true}
                    releaseId={props.releaseId}
                    displayOnlyTargetDates={false}
                    canBeSubmitted={props.canBeSubmitted}
                />
            }
            <SkipReleaseModal
                open={skipReleaseModalVisible}
                onCancel={handleSkipReleaseModalCancel}
                onConfirm={handleSkipReleaseModalConfirm}
                isAndroid={isAndroid}
                isAppleAppStore={isAppleAppStore}
            />
        </div>
    );
};

export default ReleaseActionsComponent;

const confirmationModalText: (option: EditReleaseOption, isRollback: boolean, version?: string) => string = (
    option,
    isRollback,
    version,
) => {
    switch (option) {
        case EditReleaseOption.Complete:
            return 'This action will mark this release as completed in Runway. Any automated post-completion actions (e.g. tagging) will be performed.';
        case EditReleaseOption.Uncomplete:
            return `This action will mark this release as active again in Runway. Please make sure there's no Release on the Production track in Google Play with version ${version}.`;
        case EditReleaseOption.Delete:
            return isRollback ?
                    'This action will discard this rollback release.'
                :   'This action will only delete the record from Runway. External artifacts will not be affected.';
        case EditReleaseOption.Skip:
            return 'The release record will stay in Runway but appear as skipped in the release timeline.';
        case EditReleaseOption.Unskip:
            return 'This action will mark this release as active again in Runway.';
    }
};
